import { Button } from "@mui/material";
import DOMPurify from "dompurify";
import { useMemo } from "react";
import { DialogTitle } from "components/Dialog";
import type { components } from "types/declarations/api";
import * as Styles from "./NewsArticleDialog.styles.ts";

type NewsArchiveArticle = components["schemas"]["NewsArchiveArticle"];

interface NewsArticleDialogProps {
	article: NewsArchiveArticle | null;
	onClose: () => void;
}

const sanitizeArticleBody = (body: string) => {
	const sanitizedBody = DOMPurify.sanitize(body, { ADD_ATTR: ["target"] });
	const parsedBody = new DOMParser().parseFromString(sanitizedBody, "text/html");

	parsedBody.querySelectorAll("a").forEach((link) => {
		link.target = "_blank";
		link.rel = "noopener noreferrer";
	});

	return parsedBody.body.innerHTML;
};

const getSafeFileUrl = (filePublicUrl?: string | null) => {
	if (!filePublicUrl) return undefined;

	try {
		const url = new URL(filePublicUrl);
		return url.protocol === "https:" || url.protocol === "http:" ? url.href : undefined;
	} catch {
		return undefined;
	}
};

const NewsArticleDialog = ({ article, onClose }: NewsArticleDialogProps) => {
	const sanitizedBody = useMemo(() => sanitizeArticleBody(article?.body ?? ""), [article?.body]);
	const filePublicUrl = getSafeFileUrl(article?.filePublicUrl);

	return (
		<Styles.ArticleDialog
			open={article !== null}
			onClose={onClose}
			maxWidth="lg"
			fullWidth
			scroll="paper"
			aria-labelledby="customized-dialog-title"
			aria-describedby="news-article-body"
		>
			<DialogTitle
				title={article?.title || "News article"}
				handleClose={onClose}
			/>
			<Styles.ArticleDialogContent dividers>
				{article?.subtitle && <Styles.ArticleSubtitle>{article.subtitle}</Styles.ArticleSubtitle>}
				{sanitizedBody ? (
					<Styles.ArticleBody
						id="news-article-body"
						dangerouslySetInnerHTML={{ __html: sanitizedBody }}
					/>
				) : (
					<Styles.EmptyArticleBody id="news-article-body">
						No article content is available.
					</Styles.EmptyArticleBody>
				)}
			</Styles.ArticleDialogContent>
			<Styles.ArticleDialogActions>
				{filePublicUrl && (
					<Button
						component="a"
						href={filePublicUrl}
						target="_blank"
						rel="noopener noreferrer"
						variant="contained"
					>
						View file
					</Button>
				)}
				<Button
					onClick={onClose}
					variant="outlined"
				>
					Close
				</Button>
			</Styles.ArticleDialogActions>
		</Styles.ArticleDialog>
	);
};

export default NewsArticleDialog;
